'use client';

import type React from 'react';
import { createContext, useCallback, useContext } from 'react';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';
import { cn } from '../lib/utils';

export interface FileDropzoneContextValue {
  openFilePicker: () => void;
}

const FileDropzoneContext = createContext<FileDropzoneContextValue | undefined>(undefined);

export function useFileDropzone() {
  const ctx = useContext(FileDropzoneContext);
  if (!ctx) throw new Error('useFileDropzone must be used within a FileDropzone');
  return ctx;
}

export interface FileDropzoneProps extends Omit<DropzoneOptions, 'onDrop'> {
  children: React.ReactNode;
  onFilesDrop: (files: File[]) => void;
  className?: string;
  overlayText?: string;
}

export function FileDropzone({
  children,
  onFilesDrop,
  className,
  overlayText = 'Drop files here...',
  ...dropzoneOptions
}: FileDropzoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFilesDrop(acceptedFiles);
      }
    },
    [onFilesDrop]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true, // Let children handle clicks
    noKeyboard: true,
    ...dropzoneOptions,
  });

  return (
    <FileDropzoneContext.Provider value={{ openFilePicker: open }}>
      <div {...getRootProps()} className={cn('relative', className)}>
        <input {...getInputProps()} />
        {children}

        {isDragActive && (
          <div className="absolute inset-0 z-[100] flex items-center justify-center rounded-md bg-background/80 backdrop-blur-sm border-2 border-dashed border-primary">
            <p className="text-lg font-semibold text-primary pointer-events-none drop-shadow-sm">
              {overlayText}
            </p>
          </div>
        )}
      </div>
    </FileDropzoneContext.Provider>
  );
}
